import { PROTOCOL, type RoomView } from '@uno/shared';
import { useState } from 'react';
import { useT } from '../hooks';
import { fmt, plural } from '../i18n';
import { net } from '../net';
import type { CardTheme } from '../theme/types';
import { RulesEditor } from './RulesEditor';

interface Props {
  room: RoomView;
  theme: CardTheme;
}

export function RoomLobby({ room }: Props) {
  const t = useT();
  const me = room.players.find((p) => p.id === room.self?.id);
  const isHost = me?.isHost ?? false;
  const [copied, setCopied] = useState(false);

  // Bots are always willing, so only humans gate the start.
  const humans = room.players.filter((p) => !p.isBot);
  const readyCount = humans.filter((p) => p.ready).length;
  const participants = humans.length + room.botCount;
  const enoughPlayers = participants >= PROTOCOL.MIN_PLAYERS;

  const copyLink = async () => {
    const url = `${location.origin}/r/${room.id}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      net.toast('info', url);
    }
  };

  return (
    <div className="roomlobby">
      <section className="panel">
        <div className="roomlobby__head">
          <div>
            <h1 className="roomlobby__name">{room.name}</h1>
            <p className="muted">
              {t.room.roomLabel} <code className="code">{room.id}</code>
              {room.hasPassword && ` · 🔒 ${t.room.locked}`}
              {` · ${room.isPublic ? t.room.public : t.room.unlisted}`}
            </p>
          </div>
          <div className="roomlobby__actions">
            <button type="button" className="btn btn--ghost btn--sm" onClick={copyLink}>
              {copied ? t.room.copied : t.room.copyLink}
            </button>
            <button type="button" className="btn btn--ghost btn--sm" onClick={() => net.leaveRoom()}>
              {t.common.leave}
            </button>
          </div>
        </div>

        <div className="status-strip">
          {enoughPlayers ? (
            <>
              {fmt(t.room.readyStatus, { ready: readyCount, total: humans.length })}
              {room.botCount > 0 && <> · {plural(t.room.botsJoining, room.botCount)}</>}
              {' — '}
              {t.room.startsWhenReady}
            </>
          ) : (
            fmt(t.room.needMorePlayers, { min: PROTOCOL.MIN_PLAYERS })
          )}
        </div>
      </section>

      <section className="panel">
        <h2 className="panel__title">
          {t.room.playersTitle} <span className="muted">({participants}/{room.maxPlayers})</span>
        </h2>
        <ul className="playerlist">
          {humans.map((p) => (
            <li key={p.id} className={`playerlist__row ${p.ready ? 'is-ready' : ''}`}>
              <span className={`avatar ${p.emoji ? 'avatar--emoji' : ''}`} aria-hidden="true">
                {p.emoji || p.nickname.slice(0, 1).toUpperCase()}
              </span>
              <span className="playerlist__name">
                {p.nickname}
                {p.id === room.self?.id && <span className="tag">{t.common.you}</span>}
                {p.isHost && <span className="tag tag--host">{t.common.host}</span>}
                {p.status === 'disconnected' && <span className="tag tag--warn">{t.room.offlineTag}</span>}
              </span>
              <span className={`ready-pill ${p.ready ? 'ready-pill--on' : ''}`}>
                {p.ready ? t.common.ready : t.common.notReady}
              </span>
              {isHost && p.id !== room.self?.id && (
                <button
                  type="button"
                  className="iconbtn"
                  title={fmt(t.room.removePlayer, { name: p.nickname })}
                  onClick={() => net.kick(p.id)}
                >
                  ✕
                </button>
              )}
            </li>
          ))}

          {/* Seats the bots will take. Shown so the roster reads true before the deal. */}
          {Array.from({ length: room.botCount }, (_, i) => {
            const seated = room.players.filter((p) => p.isBot)[i];
            return (
              <li key={seated?.id ?? `bot-${i}`} className="playerlist__row is-ready playerlist__row--bot">
                <span className="avatar avatar--bot" aria-hidden="true">
                  🤖
                </span>
                <span className="playerlist__name">
                  {seated?.nickname ?? fmt(t.room.botPlaceholder, { n: i + 1 })}
                  <span className="tag tag--bot">{t.common.ai}</span>
                </span>
                <span className="ready-pill ready-pill--on">{t.common.ready}</span>
                {isHost && (
                  <button
                    type="button"
                    className="iconbtn"
                    title={t.room.removeBot}
                    onClick={() => net.setBots(room.botCount - 1)}
                  >
                    ✕
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      </section>

      <section className="panel">
        <h2 className="panel__title">
          {t.room.botsTitle} {!isHost && <span className="muted">— {t.room.botsHostOnly}</span>}
        </h2>
        <div className="botbar">
          <div className="botbar__text">
            <strong>
              {room.botCount === 0 ? t.room.noBots : plural(t.room.botsWillJoin, room.botCount)}
            </strong>
            <em>
              {room.maxBots === 0 ? t.room.botsRoomFull : fmt(t.room.botsHint, { max: room.maxBots })}
            </em>
          </div>
          <div className="stepper">
            <button
              type="button"
              className="stepper__btn"
              disabled={!isHost || room.botCount <= 0}
              onClick={() => net.setBots(room.botCount - 1)}
              aria-label={t.room.removeBot}
            >
              −
            </button>
            <span className="stepper__value" aria-live="polite">
              {room.botCount}
            </span>
            <button
              type="button"
              className="stepper__btn"
              disabled={!isHost || room.botCount >= room.maxBots}
              onClick={() => net.setBots(room.botCount + 1)}
              aria-label={t.room.addBot}
            >
              +
            </button>
          </div>
        </div>
        {isHost && humans.length === 1 && room.botCount === 0 && room.maxBots > 0 && (
          <button type="button" className="btn btn--sm botbar__solo" onClick={() => net.setBots(1)}>
            {t.room.soloCta}
          </button>
        )}
      </section>

      <section className="panel">
        <h2 className="panel__title">
          {t.create.houseRules} {!isHost && <span className="muted">— {t.rules.hostOnly}</span>}
        </h2>
        <RulesEditor rules={room.rules} disabled={!isHost} onChange={(patch) => net.updateRules(patch)} />
      </section>

      <div className="readybar">
        <button
          type="button"
          className={`btn btn--lg ${me?.ready ? 'btn--ghost' : 'btn--primary'}`}
          disabled={!enoughPlayers && !me?.ready}
          onClick={() => net.setReady(!me?.ready)}
        >
          {me?.ready ? t.room.cancelReady : t.room.imReady}
        </button>
      </div>
    </div>
  );
}
